import React, { useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { MultiSelect } from "primereact/multiselect";
import { confirmDialog } from "primereact/confirmdialog";

const FormsSetsListDatatableComponent = ({
  data,
  allForms,
  onAddForms,
  onRemoveForm,
  onDeleteFormSet,
  filters,
  setFilters,
  header,
  first,
  rows,
  totalRecords,
  onPageChange,
  onSort,
  sortField,
  sortOrder,
}) => {
  const toast = useRef(null);
  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
  const [editingFormSet, setEditingFormSet] = useState(null);
  const [selectedFormsToAdd, setSelectedFormsToAdd] = useState([]);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [formSetToDelete, setFormSetToDelete] = useState(null);
  const [isPreviewDialogVisible, setIsPreviewDialogVisible] = useState(false);
  const [previewFormSet, setPreviewFormSet] = useState(null);
  const [previewSearch, setPreviewSearch] = useState("");
  const [previewPage, setPreviewPage] = useState(0);

  const PAGE_SIZE = 9;

  const openEditDialog = (formSet) => {
    setEditingFormSet(formSet);
    setIsEditDialogVisible(true);
    setSelectedFormsToAdd([]);
  };

  const hideEditDialog = () => {
    setIsEditDialogVisible(false);
    setEditingFormSet(null);
    setSelectedFormsToAdd([]);
  };

  const openDeleteDialog = (formSet) => {
    setFormSetToDelete(formSet);
    setIsDeleteDialogVisible(true);
  };

  const hideDeleteDialog = () => {
    setFormSetToDelete(null);
    setIsDeleteDialogVisible(false);
  };
  const openPreviewDialog = (formSet) => {
    setPreviewFormSet(formSet);
    setIsPreviewDialogVisible(true);
  };

  const hidePreviewDialog = () => {
    setPreviewFormSet(null);
    setIsPreviewDialogVisible(false);
  };

  const handleConfirmDelete = () => {
    if (formSetToDelete) {
      onDeleteFormSet(formSetToDelete.id);
      hideDeleteDialog();
    }
  };

  const handleAddClick = async () => {
    if (!editingFormSet || !selectedFormsToAdd.length) return;

    try {
      await onAddForms(editingFormSet.id, selectedFormsToAdd);

      // Update local state optimistically to keep dialog open
      setEditingFormSet((prev) => ({
        ...prev,
        formIds: [...(prev.formIds || []), ...selectedFormsToAdd],
      }));

      setSelectedFormsToAdd([]);

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Forms added to set successfully.",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to add forms.",
        life: 3000,
      });
    }
  };

  const handleRemoveClick = async (formId) => {
    if (!editingFormSet) return;

    try {
      await onRemoveForm(editingFormSet.id, formId);

      // Update local state optimistically to keep dialog open
      setEditingFormSet((prev) => ({
        ...prev,
        formIds: (prev.formIds || []).filter((id) => id !== formId),
      }));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Form removed from set successfully.",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to remove form.",
        life: 3000,
      });
    }
  };

  const confirmRemoveForm = (form) => {
    confirmDialog({
      message: `Are you sure you want to remove the form "${form.label}"?`,
      header: "Confirm Removal",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-text",
      accept: () => handleRemoveClick(form.id),
    });
  };

  const formsInCurrentSet = React.useMemo(() => {
    if (!editingFormSet || !allForms?.length) return [];
    const idSet = new Set(editingFormSet.formIds || []);
    return allForms.filter((form) => idSet.has(form.id));
  }, [editingFormSet, allForms]);

  const availableFormsForAdding = React.useMemo(() => {
    if (!editingFormSet || !allForms?.length) return [];
    const idSet = new Set(editingFormSet.formIds || []);
    return allForms.filter((form) => !idSet.has(form.id));
  }, [editingFormSet, allForms]);

  const formsInPreviewSet = React.useMemo(() => {
    if (!previewFormSet || !allForms?.length) return [];
    const idSet = new Set(previewFormSet.formIds || []);
    return allForms.filter((form) => idSet.has(form.id));
  }, [previewFormSet, allForms]);

  React.useEffect(() => {
    setPreviewPage(0);
  }, [isPreviewDialogVisible, previewSearch]);

  const filteredFormsInPreviewSet = React.useMemo(() => {
    if (!formsInPreviewSet) return [];
    if (!previewSearch.trim()) return formsInPreviewSet;
    const search = previewSearch.toLowerCase();
    return formsInPreviewSet.filter(
      (f) =>
        f.label?.toLowerCase().includes(search) ||
        f.id?.toString().toLowerCase().includes(search)
    );
  }, [formsInPreviewSet, previewSearch]);

  const paginatedFormsInPreviewSet = React.useMemo(() => {
    const start = previewPage * PAGE_SIZE;
    return filteredFormsInPreviewSet.slice(start, start + PAGE_SIZE);
  }, [filteredFormsInPreviewSet, previewPage]);

  const editDialogFooter = (
    <Button
      label="Close"
      icon="pi pi-times"
      onClick={hideEditDialog}
      className="p-button-text"
      style={{
        backgroundColor: "#D97A07",
        border: "none",
        color: "#fff",
        fontFamily: "poppins",
      }}
    />
  );

  const deleteDialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideDeleteDialog}
        className="p-button-text"
        style={{
          backgroundColor: "#D97A07",
          border: "none",
          color: "#fff",
          fontFamily: "poppins",
        }}
      />
      <Button
        label="Confirm"
        icon="pi pi-check"
        onClick={handleConfirmDelete}
        style={{
          backgroundColor: "#0D2E4D",
          border: "none",
          color: "#fff",
          fontFamily: "poppins",
        }}
        autoFocus
      />
    </div>
  );

  const previewDialogFooter = (
    <Button
      label="Close"
      icon="pi pi-times"
      onClick={hidePreviewDialog}
      className="p-button-text"
      style={{
        backgroundColor: "#D97A07",
        border: "none",
        color: "#fff",
        fontFamily: "poppins",
      }}
    />
  );

  const nameBodyTemplate = (rowData) => (
    <div
      className="formset-name-cell clickable-column"
      role="button"
      tabIndex={0}
      onClick={() => openPreviewDialog(rowData)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPreviewDialog(rowData);
      }}
      style={{
        cursor: "pointer",
        color: "#0D2E4D",
        fontWeight: 600,
        fontFamily: "poppins",
        width: "100%",
        height: "100%",
        padding: "8px 0",
        transition: "background 0.2s, color 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F6F6F6")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {rowData.name}
    </div>
  );

  const actionsBodyTemplate = (rowData) => (
    <div className="actions">
      <Button
        icon="pi pi-eye"
        className="p-mr-2"
        onClick={() => openPreviewDialog(rowData)}
        tooltip="Preview Forms"
        tooltipOptions={{ position: "top" }}
        style={{
          backgroundColor: "#fff",
          border: "none",
          color: "#0D2E4D",
        }}
      />
      <Button
        icon="pi pi-pencil"
        className="p-mr-2"
        onClick={() => openEditDialog(rowData)}
        tooltip="Edit Forms in Set"
        tooltipOptions={{ position: "top" }}
        style={{
          backgroundColor: "#fff",
          border: "none",
          color: "#D97A07",
        }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-danger"
        onClick={() => openDeleteDialog(rowData)}
        tooltip="Delete Formset"
        tooltipOptions={{ position: "top" }}
        style={{
          backgroundColor: "#fff",
          border: "none",
          color: "#b30202ff",
        }}
      />
    </div>
  );

  return (
    <div className="table-container">
      <Toast ref={toast} />
      <DataTable
        key="formset-table"
        dataKey="id"
        style={{ fontFamily: "poppins" }}
        value={data}
        paginator={false}
        responsiveLayout="scroll"
        filters={filters}
        globalFilterFields={["id", "name"]}
        header={header}
        emptyMessage="No formsets found."
        onFilter={(e) => setFilters(e.filters)}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={onSort}
      >
        <Column
          field="name"
          header="Name"
          style={{ width: "75%" }}
          sortable
          body={nameBodyTemplate}
        />
        <Column
          header="Actions"
          body={actionsBodyTemplate}
          style={{ width: "25%", textAlign: "center" }}
        />
      </DataTable>

      <Paginator
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
      />

      <Dialog
        header={`Edit Forms in "${editingFormSet?.name}"`}
        visible={isEditDialogVisible}
        style={{ width: "80vw", minHeight: "90vh" }}
        footer={editDialogFooter}
        onHide={hideEditDialog}
        modal
      >
        <div className="p-fluid">
          <h5>Add Forms to Set</h5>
          <div
            className="p-grid p-align-center"
            style={{ marginBottom: "20px" }}
          >
            <div className="p-col-9">
              <MultiSelect
                value={selectedFormsToAdd}
                options={availableFormsForAdding}
                onChange={(e) => setSelectedFormsToAdd(e.value)}
                optionLabel="label"
                optionValue="id"
                placeholder="Select Forms to Add"
                filter
                className="w-full"
                style={{ width: "100%" }}
              />
            </div>
            <div className="p-col-3 mt-2">
              <Button
                label="Add"
                icon="pi pi-plus"
                onClick={handleAddClick}
                disabled={selectedFormsToAdd.length === 0}
                style={{
                  backgroundColor: "#0D2E4D",
                  border: "none",
                  color: "#fff",
                  fontFamily: "poppins",
                }}
              />
            </div>
          </div>

          <hr />

          <h5>Forms Currently in Set</h5>
          <DataTable
            value={formsInCurrentSet}
            emptyMessage="No forms in this set."
            style={{ marginTop: "10px" }}
          >
            <Column field="label" header="Form Label" />
            <Column
              header="Action"
              style={{ width: "100px", textAlign: "center" }}
              body={(rowData) => (
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-danger"
                  onClick={() => confirmRemoveForm(rowData)}
                  tooltip="Remove from set"
                  tooltipOptions={{ position: "top" }}
                  style={{
                    backgroundColor: "#fff",
                    border: "none",
                    color: "#D97A07",
                  }}
                />
              )}
            />
          </DataTable>
        </div>
      </Dialog>

      <Dialog
        header={`Forms in "${previewFormSet?.name || ""}"`}
        visible={isPreviewDialogVisible}
        style={{
          width: "80vw",
          height: "90vh",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        footer={previewDialogFooter}
        onHide={hidePreviewDialog}
        modal
      >
        <div style={{ marginBottom: "1rem" }}>
          <input
            type="text"
            value={previewSearch}
            onChange={(e) => setPreviewSearch(e.target.value)}
            placeholder="Search forms..."
            style={{
              width: "100%",
              padding: "8px",
              fontFamily: "poppins",
              borderRadius: "4px",
              border: "1px solid #ccc",
              marginBottom: "10px",
            }}
          />
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <div style={{ flex: 1, overflowY: "auto" }}>
            <DataTable
              value={paginatedFormsInPreviewSet}
              emptyMessage="No forms in this set."
              style={{ fontFamily: "poppins", height: "100%" }}
              scrollable
              scrollHeight="100%"
            >
              <Column field="label" header="Form Label" />
              <Column field="id" header="Form ID" />
              <Column
                header="Action"
                style={{ width: "100px", textAlign: "center" }}
                body={(rowData) => (
                  <Button
                    icon="pi pi-external-link"
                    className="p-button p-button-success"
                    style={{
                      backgroundColor: "#fff",
                      color: "#0d2e4d",
                      border: "none",
                      transition: "border 0.3s ease",
                    }}
                    onClick={() => {
                      const user = JSON.parse(
                        localStorage.getItem("userData") || "{}"
                      );
                      const isAdmin = user.roles?.some(
                        (role) => role.name === "ROLE_ADMIN"
                      );
                      const url = isAdmin
                        ? `/form/${rowData.id}`
                        : `/User-Created-Form-Fill/${rowData.id}`;
                      window.open(url, "_blank");
                    }}
                    tooltip="Open Form"
                    tooltipOptions={{ position: "top" }}
                  />
                )}
              />
            </DataTable>
          </div>
          <div style={{ marginTop: "10px" }}>
            <Paginator
              rows={PAGE_SIZE}
              totalRecords={filteredFormsInPreviewSet.length}
              first={previewPage * PAGE_SIZE}
              onPageChange={(e) => setPreviewPage(e.page)}
              pageLinkSize={3}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              style={{ justifyContent: "center" }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Confirm Deletion"
        visible={isDeleteDialogVisible}
        style={{ width: "30vw" }}
        modal
        footer={deleteDialogFooter}
        onHide={hideDeleteDialog}
      >
        <div className="confirmation-content p-d-flex p-ai-center">
          <i
            className="pi pi-exclamation-triangle p-mr-3"
            style={{ fontSize: "2rem" }}
          />
          {formSetToDelete && (
            <span>
              Are you sure you want to delete <b>{formSetToDelete.name}</b>?
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default FormsSetsListDatatableComponent;
